import type { DocumentVersionORM } from 'src/types/orms';
import type { GroupDraft } from 'src/components/SlideSelector/PageGroupList';
import type { ThumbnailPage } from 'src/components/SlideSelector/SlideSelector';
import type { PayloadGroup } from 'src/components/PresentationBuilder/state/PresentationBuilderStateProvider';
import { sortGroups } from '../groupHelpers';

// Preserve this order at the call site: filterSelectedSlides, addAssociatedSlides, addRequiredSlides.
export function filterSelectedSlides (groupDrafts: GroupDraft[]) {
  const allSelected = groupDrafts.filter(({ checked, name, pages }) =>
    checked || (!name && pages.some(page => page.checked)));

  const allGroups:GroupDraft[] = [];
  const allSingleSlides:GroupDraft[] = [];
  allSelected.forEach(group => {
    if (group.pages.length > 1) allGroups.push(group);
    else allSingleSlides.push(group);
  });
  // sort the groups by document order (first slide number)
  allGroups.sort(sortGroups());
  return [...allGroups, ...allSingleSlides];
}

export const addAssociatedSlides = (
  groupDrafts: GroupDraft[],
  pages: ThumbnailPage[],
  selectedGroups: PayloadGroup[],
  associatedParentsMap: ReadonlyMap<string, boolean>,
): { groups: GroupDraft[]; associatedParentsMap: Map<string, boolean> } => {
  const associatedSlideIds = new Set<string>();
  // Map through groupDraft and add the associated slides to associatedSlides set
  groupDrafts.forEach(groupDraft => {
    groupDraft.pages.forEach(page => {
      page.linkedSlides?.forEach(slideId => {
        associatedSlideIds.add(slideId);
      });
    });
  });
  // Map through groupDrafts pages and remove the id from associatedSlideIds
  groupDrafts.forEach(groupDraft => {
    groupDraft.pages.forEach(page => {
      associatedSlideIds.delete(page.pageId);
    });
  });
  // Map through selectedGroups and remove the id from associatedSlideIds
  selectedGroups.forEach(group => {
    group.pages.forEach(page => {
      associatedSlideIds.delete(page.pageId);
    });
  });

  const result: GroupDraft[] = [];
  const associatedParents = new Map(associatedParentsMap);
  // Map through associated slide ids and convert it into an usable object
  associatedSlideIds.forEach(slideId => {
    const page = pages.find(page => page.pageId === slideId);
    if (page) {
      associatedParents.set(page.pageId, true);
      result.push({ id: page.pageId, pages: [page] });
    }
  });

  const allPayloadGroups:GroupDraft[] = [];
  const singleSelectedSlides:GroupDraft[] = [];
  groupDrafts.forEach(groupDraft => {
    const { pages:{ length } } = groupDraft;
    length > 1
      ? allPayloadGroups.push(groupDraft)
      : singleSelectedSlides.push(groupDraft);
  });

  // Sorting all slides: Group first and then the rest in slide number order
  const sortedByPageNum = [...singleSelectedSlides, ...result]
    .sort((a, b) => (a.pages[0].number - b.pages[0].number));

  return {
    groups: [...allPayloadGroups, ...sortedByPageNum],
    associatedParentsMap: associatedParents,
  };
};

export const addRequiredSlides = (
  groupDrafts: GroupDraft[],
  pages: ThumbnailPage[],
  selectedGroups: PayloadGroup[],
  selectedDocumentVersionORM?: DocumentVersionORM,
): GroupDraft[] => {
  const selectedPageIds = new Set([
    ...groupDrafts.flatMap(groupDraft => groupDraft.pages.map(page => page.pageId)),
    ...selectedGroups
      .filter(group => group.documentVersionORM?.model.id === selectedDocumentVersionORM?.model.id)
      .flatMap(group => group.pages.map(page => page.pageId)),
  ]);

  const requiredSlides = pages
    .filter(page => page.isRequired && !selectedPageIds.has(page.pageId))
    .map<GroupDraft>(page => ({
      id: page.pageId,
      pages: [page],
    }));

  return [...groupDrafts, ...requiredSlides].sort(sortGroups());
};
